import React, { useState, useEffect } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';
import { 
  ShieldAlert, Lock, Terminal, Download, Copy, Check, 
  ArrowLeft, Sparkles, FileText, Upload, RefreshCw, FileCheck, X
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import { getFieldLabel, getFieldValue, getNotesText } from '../utils/demoPresetsI18n';

interface CyberAgentViewProps {
  agentId: string;
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
}

const DEFAULT_CYBER_DEMO: Record<string, {
  field1LabelUk: string; field1Value: string;
  field2LabelUk: string; field2Value: string;
  field3LabelUk: string; field3Value: string;
  field4LabelUk: string; field4Value: string;
  field5LabelUk: string; field5Value: string;
  field6LabelUk: string; field6Value: string;
  notesText: string;
}> = {
  'cyber-kszi-auditor': {
    field1LabelUk: 'Назва об\'єкта інформаційної діяльності', field1Value: 'Автоматизована система АС-2 державних реєстрів',
    field2LabelUk: 'Категорія об\'єкта критичної інфраструктури (КІІ)', field2Value: 'II категорія критичності (Держреєстри / Енергетика)',
    field3LabelUk: 'Гриф та клас конфіденційності інформації', field3Value: 'Службова інформація (СІО) + Персональні дані',
    field4LabelUk: 'Поточний статус КСЗІ & Атестат', field4Value: 'Потрібна первинна побудова КСЗІ та атестація ДССЗЗІ',
    field5LabelUk: 'Нормативно-технічна база аудиту', field5Value: 'НД ТЗЗІ 2.5-005-99, НД ТЗЗІ 2.5-010-03, ЗУ № 2163-VIII',
    field6LabelUk: 'Наявний комплекс засобів захисту', field6Value: 'Апаратний файрвол Fortinet, антивірус, МФА, VPN ГРІФ',
    notesText: 'Провести комплексний аудит інформаційної системи АС-2 на відповідність вимогам ДССЗЗІ, розробити Модель загроз та Модель порушника, скласти Технічне завдання (ТЗ) на створення КСЗІ та підготувати проєкт Положення про захист інформації.'
  },
  'cyber-iso27001-nis2-manager': {
    field1LabelUk: 'Галузь підприємства & Регіон діяльності', field1Value: 'FinTech / Банківський сектор & Клауд-провайдер (UA & EU)',
    field2LabelUk: 'Цільовий стандарт відповідності', field2Value: 'ISO/IEC 27001:2022 + EU NIS2 Directive (Essential entity)',
    field3LabelUk: 'Кількість персоналу & Серверних вузлів', field3Value: '120 співробітників / 45 хмарних серверів (AWS & Hetzner)',
    field4LabelUk: 'Поточна наявність ISMS (СУІБ)', field4Value: 'Політики частково розроблені (потрібен Gap-аналіз)',
    field5LabelUk: 'Основні критичні IT-активи', field5Value: 'Бази даних клієнтів (PostgreSQL), API-шлюзи, CI/CD',
    field6LabelUk: 'Термін проходження інспекційного аудиту', field6Value: 'IV квартал 2026 року (Сертифікація BSI / TÜV)',
    notesText: 'Провести Gap-аналіз готовності СУІБ до ISO 27001:2022 та Директиви NIS2, розробити Заяву про застосовність (Statement of Applicability — SoA), сформувати реєстр ризиків кібербезпеки та розробити Політику управління інцидентами.'
  },
  'cyber-incident-soc-analyst': {
    field1LabelUk: 'Джерело логів та тип кібер-атаки', field1Value: 'WAF / Nginx access.log + SIEM Wazuh / DDoS + SQLi',
    field2LabelUk: 'Часовий інтервал аномальної активності', field2Value: '02.08.2026 з 14:20 по 15:45 (GMT+3)',
    field3LabelUk: 'Виявлені IP-адреси нападників (IoC)', field3Value: '185.220.101.4, 45.142.214.88 (Tor exit nodes)',
    field4LabelUk: 'Цільові URL-ендпоінти атак', field4Value: 'POST /api/v1/auth/login (Brute-force 12 000 req/min)',
    field5LabelUk: 'Вплив на інфраструктуру & Статус доступності', field5Value: 'Навантаження CPU 98%, збої відповідей 502 HTTP',
    field6LabelUk: 'Формат звітності та нотифікації', field6Value: 'Стандарт картки інциденту CERT-UA (TLP:AMBER)',
    notesText: 'Парсинг логів WAF/SIEM зафіксував масовану розподілену атаку типу HTTP Flood та спроби впровадження SQL-ін\'єкцій на форму авторизації. Необхідно надати правила блокування (Fail2ban, WAF rules), провести триаж IoC та підготувати терміновий звіт для CERT-UA.'
  },
  'system-architecture-agent': {
    field1LabelUk: 'Назва системи & Основне призначення', field1Value: 'High-Load FinTech API / Реєстр Транзакцій',
    field2LabelUk: 'Очікуваний потік навантаження (QPS / DAU)', field2Value: '5 000 QPS (пік 15 000 QPS) / 500 000 DAU',
    field3LabelUk: 'Поточний стек технологій & Сховища даних', field3Value: 'Node.js Express + PostgreSQL (1.5 TB) + Redis',
    field4LabelUk: 'Цільовий рівень доступності (SLA & Latency)', field4Value: 'SLA 99.99% (p99 latency < 150ms)',
    field5LabelUk: 'Вузькі місця & Проблемні зони', field5Value: 'Lock-конфлікти при масових списаннях в DB, таймаути',
    field6LabelUk: 'Інфраструктура розгортання', field6Value: 'Kubernetes Cluster (GKE / AWS EKS) + On-Premise Backup',
    notesText: 'Провести комплексний аудит архітектури системного дизайну. Розрахувати необхідну пропускну здатність, запропонувати стратегію масштабування PostgreSQL (Master-Replica, Partitioning/Sharding), розробити схему двоуровневого кешування (Redis Cache-Aside), схему черг задач (Kafka/RabbitMQ) та згенерувати Mermaid-діаграму цільової High-Load інфраструктури.'
  },
  'public-api-integrator': {
    field1LabelUk: 'Категорія сервісу & Задача підприємства', field1Value: 'Авто-отримання курсів валют, геокодування адрес та погодних умов',
    field2LabelUk: 'Цільовий мова / Фреймворк розробки', field2Value: 'TypeScript / Node.js (Express) + React PWA',
    field3LabelUk: 'Вимоги до авторизації & Безкоштовності', field3Value: 'Strictly Free Public APIs (No Auth / Без платних ключів)',
    field4LabelUk: 'Очікувана частота запитів (Rate Limit)', field4Value: 'До 100 запитів / хвилину (без перевищення лімітів)',
    field5LabelUk: 'Потребні формати даних & CORS', field5Value: 'JSON response, HTTPS Enabled, CORS Allowed',
    field6LabelUk: 'Критичність доступності (SLA / Fallbacks)', field6Value: '99.5% uptime + квоти резервних дзеркал (Fallback endpoints)',
    notesText: 'Підібрати 3 безкоштовні публічні REST/JSON API з каталогу public-apis (наприклад: Monobank/НБУ API для фінансів, OpenStreetMap Nominatim для геокодингу, Open-Meteo для погоди). Згенерувати готовий TypeScript клас-модуль з викликами fetch(), обробкою помилок, кешуванням результатів та прикладом React-компонента.'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація мультиагентної команди Кібербезпеки (SOC & ISO Audit)...",
  "Аналіз логів, моделей загроз та нормативів КСЗІ / ISO 27001 / NIS2...",
  "Перевірка вразливостей, IoC та розрахунок матриці ризиків СУІБ...",
  "Розробка правил нейтралізації атак та політик безпеки...",
  "Формування підсумкового Звіту з Кібербезпеки за стандартами CERT-UA та ДСТУ 4163..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <ShieldAlert className={className} />;
  return <IconComponent className={className} />;
};

export default function CyberAgentView({
  agentId,
  language,
  onBack, onGoHome
}: CyberAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Кібер-Аналітичний Модуль',
    nameEn: 'Cyber AI Agent',
    descriptionUk: 'Аудит кібербезпеки та комплаєнсу.',
    descriptionEn: 'Cybersecurity audit and compliance.',
    icon: 'ShieldAlert',
    categoryUk: 'КІБЕРБЕЗПЕКА & IT',
    categoryEn: 'CYBERSECURITY & IT'
  };

  const defaultDemo = DEFAULT_CYBER_DEMO[agentId] || DEFAULT_CYBER_DEMO['cyber-kszi-auditor'];

  const [field1, setField1] = useState(getFieldValue(defaultDemo, 1, language));
  const [field2, setField2] = useState(getFieldValue(defaultDemo, 2, language));
  const [field3, setField3] = useState(getFieldValue(defaultDemo, 3, language));
  const [field4, setField4] = useState(getFieldValue(defaultDemo, 4, language));
  const [field5, setField5] = useState(getFieldValue(defaultDemo, 5, language));
  const [field6, setField6] = useState(getFieldValue(defaultDemo, 6, language));
  const [notesText, setNotesText] = useState(getNotesText(defaultDemo, language));

  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    setField1(getFieldValue(defaultDemo, 1, language));
    setField2(getFieldValue(defaultDemo, 2, language));
    setField3(getFieldValue(defaultDemo, 3, language));
    setField4(getFieldValue(defaultDemo, 4, language));
    setField5(getFieldValue(defaultDemo, 5, language));
    setField6(getFieldValue(defaultDemo, 6, language));
    setNotesText(getNotesText(defaultDemo, language));
    setReportResult(null);
  }, [defaultDemo, language]);

  useEffect(() => {
    if (reportResult && typeof reportResult === 'string' && reportResult.trim().length > 0 && !reportResult.startsWith('⚠️ ПОМИЛКА')) {
      autoDownloadReport(reportResult, agentConfig.nameUk || 'Кібербезпека-Звіт', agentId);
    }
  }, [reportResult]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', { method: 'POST', body: formData });

        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
        } else {
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл ${file.name} додано]` }]);
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setIsReadingFile(false);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunAnalysis = async () => {
    setIsProcessing(true);
    setReportResult(null);
    setLoadingStepIndex(0);

    const stepInterval = setInterval(() => {
      setLoadingStepIndex(prev => (prev < LOADING_STEPS_UK.length - 1 ? prev + 1 : prev));
    }, 2500);

    try {
      const combinedDocText = attachedFiles.map(f => `--- ФАЙЛ: ${f.name} ---\n${f.text}`).join('\n\n');
      
      const payload = {
        agentId,
        userQuery: notesText,
        language,
        parameters: {
          [defaultDemo.field1LabelUk]: field1,
          [defaultDemo.field2LabelUk]: field2,
          [defaultDemo.field3LabelUk]: field3,
          [defaultDemo.field4LabelUk]: field4,
          [defaultDemo.field5LabelUk]: field5,
          [defaultDemo.field6LabelUk]: field6,
          "Додаткові документи": combinedDocText
        }
      };

      const res = await fetch('/api/generate-agent-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.report || data.content)) {
        setReportResult(data.report || data.content || 'Звіт успішно згенеровано!');
      } else {
        const errorText = data.error || data.reason || `Не вдалося отримати відповідь бекенду (HTTP ${res.status}).`;
        setReportResult(`⚠️ ПОМИЛКА ГЕНЕРАЦІЇ: ${errorText}`);
      }
    } catch (err: any) {
      setReportResult(`⚠️ ПОМИЛКА: ${err.message || 'Збій мережі'}`);
    } finally {
      clearInterval(stepInterval);
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono">
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? '← Powrót do Cyberbezpieczeństwa' : isUk ? '← До Центру Кібербезпеки' : '← Back to Cyber Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded-md">
            🔒 {getLocalizedAgentCategory(agentConfig, language)}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-violet-500/20 text-violet-300 border border-violet-400/30 rounded-md">
            {isPl ? 'NIS2 • ISO 27001 • CSIRT / CERT' : 'КСЗІ • КІІ • NIS2 • CERT-UA'}
          </span>
        </div>
      </div>

      <div className="bg-slate-950 border border-red-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-4">
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="p-4 bg-red-950/80 border border-red-500/40 rounded-2xl text-red-400 shrink-0 shadow-lg">
            <AgentIcon name={agentConfig.icon} className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/30 text-red-200 border border-red-400/40 rounded">
                {getLocalizedAgentCategory(agentConfig, language)}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide text-white">
              {getLocalizedAgentName(agentConfig, language)}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
              {getLocalizedAgentDesc(agentConfig, language)}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 text-white shadow-xl">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-red-300 flex items-center gap-2 border-b border-slate-800 pb-3">
          <ShieldAlert className="w-4 h-4 text-red-400" />
          <span>{isPl ? 'PARAMETRY SYSTEMU I CYBERBEZPIECZEŃSTWA:' : isUk ? 'ВХІДНІ ПАРАМЕТРИ СИСТЕМИ ТА КІБЕРБЕЗПЕКИ:' : 'CYBERSECURITY PARAMETERS:'}</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">1. {getFieldLabel(defaultDemo, 1, language)}</label>
            <input
              type="text"
              list="datalist-cyber-f1"
              value={field1}
              onChange={e => setField1(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz własną wartość..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-red-500 focus:outline-none font-mono"
            />
            <datalist id="datalist-cyber-f1">
              <option value="НД ТЗІ 2.5-004-99 (Класифікація автоматизованих систем)" />
              <option value="Постанова КМУ № 1109 (Категоризація об'єктів КІІ)" />
              <option value="НД ТЗІ 1.1-002-99 (Загальні положення ТЗІ)" />
              <option value="ISO/IEC 27001:2022 (СУІБ)" />
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">2. {getFieldLabel(defaultDemo, 2, language)}</label>
            <input
              type="text"
              list="datalist-cyber-f2"
              value={field2}
              onChange={e => setField2(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-red-500 focus:outline-none font-mono"
            />
            <datalist id="datalist-cyber-f2">
              <option value="ISO/IEC 27001:2022 (СУІБ)" />
              <option value="EU NIS2 Directive (Directive 2022/2555)" />
              <option value="NIST Cybersecurity Framework (CSF 2.0)" />
              <option value="SOC 2 Type II Compliance" />
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">3. {getFieldLabel(defaultDemo, 3, language)}</label>
            <input
              type="text"
              list="datalist-cyber-f3"
              value={field3}
              onChange={e => setField3(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-red-500 focus:outline-none font-mono"
            />
            <datalist id="datalist-cyber-f3">
              <option value="Рівень Г2 (Захист конфіденційної інформації)" />
              <option value="Рівень Г3 (Захист таємної інформації)" />
              <option value="Об'єкт КІІ 1-ї категорії критичності" />
              <option value="Об'єкт КІІ 2-ї категорії критичності" />
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">4. {getFieldLabel(defaultDemo, 4, language)}</label>
            <input
              type="text"
              list="datalist-cyber-f4"
              value={field4}
              onChange={e => setField4(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-red-500 focus:outline-none font-mono"
            />
            <datalist id="datalist-cyber-f4">
              <option value="Syslog / WAF / Nginx access log (W3C Standard)" />
              <option value="Windows Event Log (EVTX / Security Channel)" />
              <option value="Fortinet FortiGate Firewall Logs" />
              <option value="AWS CloudTrail & VPC Flow Logs" />
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">5. {getFieldLabel(defaultDemo, 5, language)}</label>
            <input
              type="text"
              list="datalist-cyber-f5"
              value={field5}
              onChange={e => setField5(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-red-500 focus:outline-none font-mono"
            />
            <datalist id="datalist-cyber-f5">
              <option value="Стандарт CERT-UA (TLP:AMBER / TLP:CLEAR)" />
              <option value="STIX/TAXII Threat Intelligence" />
              <option value="MITRE ATT&CK Framework v14" />
              <option value="Атестат відповідності ДССЗЗІ (Дійсний 5 років)" />
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">6. {getFieldLabel(defaultDemo, 6, language)}</label>
            <input
              type="text"
              list="datalist-cyber-f6"
              value={field6}
              onChange={e => setField6(e.target.value)}
              placeholder={isPl ? "Wybierz z listy lub wpisz..." : isUk ? "Оберіть зі списку або введіть своє значення..." : "Select from list or enter custom value..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-red-500 focus:outline-none font-mono"
            />
            <datalist id="datalist-cyber-f6">
              <option value="ДП ДССЗЗІ Сертифікована Лабораторія" />
              <option value="Незалежний орган сертифікації BSI / TÜV SÜD" />
              <option value="CERT-UA Команда реагування на комп'ютерні надзвичайні події" />
            </datalist>
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">7. ТЕХНІЧНЕ ЗАВДАННЯ ТА ЛОГИ АТАКИ/СУІБ:</label>
          <textarea rows={4} value={notesText} onChange={e => setNotesText(e.target.value)} className="w-full p-3.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-red-500 focus:outline-none font-mono leading-relaxed" />
        </div>

        <div className="space-y-3 pt-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center gap-2">
              <Upload className="w-4 h-4 text-red-400" />
              <span>СУПРОВІДНІ ФАЙЛИ ЛОГІВ ТА ПОЛІТИК (LOGS / PDF / JSON / TXT):</span>
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2.5 bg-red-950/80 hover:bg-red-900 border border-red-500/40 text-red-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4 text-red-400" />
              <span>{isReadingFile ? 'Зчитування...' : '➕ Завантажити логи/файли'}</span>
              <input type="file" multiple accept=".log,.pdf,.docx,.json,.txt" onChange={handleFileUpload} className="hidden" />
            </label>

            {attachedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200">
                <FileCheck className="w-3.5 h-3.5 text-red-400" />
                <span className="font-mono text-[11px] max-w-[150px] truncate">{file.name}</span>
                <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-400 p-0.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={handleRunAnalysis}
            disabled={isProcessing}
            className="w-full sm:w-auto px-8 py-3.5 bg-red-600 hover:bg-red-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{LOADING_STEPS_UK[loadingStepIndex]}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>🚀 ЗАПУСТИТИ КІБЕР-АНАЛІЗ</span>
              </>
            )}
          </button>
        </div>
      </div>

      {reportResult && (
        <div className="bg-slate-950 border border-red-500/40 rounded-2xl p-6 sm:p-8 space-y-4 text-white shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-red-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-red-400" />
              <span>ЗВІТ ТА ЗАКЛЮЧЕННЯ З КІБЕРБЕЗПЕКИ:</span>
            </h3>

            <button
              onClick={() => {
                navigator.clipboard.writeText(reportResult);
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
              }}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
              <span>{isCopied ? 'Скопійовано!' : 'Копіювати звіт'}</span>
            </button>
          </div>

          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-5 text-xs font-mono leading-relaxed text-slate-200 whitespace-pre-wrap max-h-[600px] overflow-y-auto">
            {reportResult}
          </div>
        </div>
      )}
    </div>
  );
}
